import { ArrowForward, CheckCircle } from '@mui/icons-material';
import { Button } from 'finara-components';

import { PaymentRequest } from '@/types/payment';

interface PaymentSuccessProps {
  paymentRequest: PaymentRequest;
  onNewPayment: () => void;
  onViewOperations: () => void;
}

export const PaymentSuccess: React.FC<PaymentSuccessProps> = ({
  paymentRequest,
  onNewPayment,
  onViewOperations,
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(amount);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className="max-w-2xl mx-auto text-center">
      {/* Success Icon */}
      <div
        className={`w-24 h-24 ${
          paymentRequest.status === 'pending' ? 'bg-blue-500/20' : 'bg-green-500/20'
        } rounded-full flex items-center justify-center mx-auto mb-6`}
      >
        <CheckCircle
          className={paymentRequest.status === 'pending' ? 'text-blue-600' : 'text-green-600'}
          style={{ fontSize: '3rem' }}
        />
      </div>

      {/* Success Message */}
      <h2
        className={`text-3xl font-bold ${
          paymentRequest.status === 'pending' ? 'text-blue-600' : 'text-green-600'
        } mb-2`}
      >
        {paymentRequest.status === 'pending' ? '¡Pago Programado!' : '¡Pago Exitoso!'}
      </h2>
      <p className="text-muted mb-8">
        {paymentRequest.status === 'pending'
          ? 'Tu pago ha sido programado y se procesará en la fecha indicada'
          : 'Tu pago ha sido procesado y acreditado exitosamente'}
      </p>

      {/* Payment Details */}
      <div className="bg-surface border border-border rounded-lg p-6 mb-8 text-left">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-muted">Referencia de Operación</div>
                <div className="font-bold text-brand-white">{paymentRequest.operationReference}</div>
              </div>

              <div>
                <div className="text-sm text-muted">
                  {paymentRequest.status === 'pending'
                    ? 'Fecha de Ejecución Programada'
                    : 'Fecha de Procesamiento'}
                </div>
                <div className="font-medium text-brand-white">
                  {paymentRequest.status === 'pending' &&
                  paymentRequest.data.scheduledDate &&
                  paymentRequest.data.scheduledTime
                    ? `${paymentRequest.data.scheduledDate} ${paymentRequest.data.scheduledTime}`
                    : paymentRequest.processedAt && formatDateTime(paymentRequest.processedAt)}
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-muted">Beneficiario</div>
                <div className="font-medium text-brand-white">
                  {paymentRequest.data.beneficiary.fullName}
                </div>
              </div>

              <div>
                <div className="text-sm text-muted">Monto</div>
                <div className="font-bold text-brand-white text-xl">
                  {formatCurrency(paymentRequest.data.amount)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Button
          variant="white"
          size="large"
          endIcon={<ArrowForward />}
          onClick={onViewOperations}
          className="border-border"
        >
          Salir
        </Button>

        <Button variant="black" size="large" onClick={onNewPayment}>
          Realizar Otro Pago
        </Button>
      </div>
    </div>
  );
};
